import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ClipboardCheck,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Circle,
  Calendar,
  User,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

export const InstallationChecklist = ({ customerId, customerName }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState('');
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');

  useEffect(() => {
    fetchChecklist();
  }, [customerId]);

  const fetchChecklist = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers/${customerId}/checklist`);
      setChecklist(response.data);
    } catch (error) {
      toast.error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    setAdding(true);
    try {
      const response = await api.post(`/customers/${customerId}/checklist`, { title: newTask.trim() });
      setChecklist(prev => ({
        ...prev,
        items: [...(prev?.items || []), response.data],
      }));
      setNewTask('');
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (itemId, currentCompleted) => {
    setTogglingId(itemId);
    try {
      await api.patch(`/customers/${customerId}/checklist/${itemId}`, { completed: !currentCompleted });
      setChecklist(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.id === itemId
            ? {
                ...item,
                completed: !currentCompleted,
                completed_at: !currentCompleted ? new Date().toISOString() : null,
                completed_by: !currentCompleted ? 'You' : null,
              }
            : item
        ),
      }));
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await api.delete(`/customers/${customerId}/checklist/${itemId}`);
      setChecklist(prev => ({
        ...prev,
        items: prev.items.filter(item => item.id !== itemId),
      }));
      toast.success('Task removed');
    } catch (error) {
      toast.error('Failed to remove task');
    }
  };

  const handleDateEdit = async (itemId) => {
    if (!editDateValue) return;
    try {
      const isoDate = new Date(editDateValue).toISOString();
      await api.patch(`/customers/${customerId}/checklist/${itemId}/date`, { completed_at: isoDate });
      setChecklist(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.id === itemId ? { ...item, completed_at: isoDate } : item
        ),
      }));
      setEditingDateId(null);
      toast.success('Date updated');
    } catch (error) {
      toast.error('Failed to update date');
    }
  };

  const items = checklist?.items || [];
  const completedCount = items.filter(i => i.completed).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="installation-checklist">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-600" />
              Installation Checklist
            </CardTitle>
            <CardDescription>
              Track installation tasks for {customerName}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-sm" data-testid="checklist-progress-badge">
              {completedCount}/{totalCount} complete
            </Badge>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-1.5">
            <span>Progress</span>
            <span className="font-medium">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" data-testid="checklist-progress-bar" />
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Checklist items */}
        <div className="space-y-0.5" data-testid="checklist-items">
          {items.map((item, idx) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all group ${
                item.completed
                  ? 'bg-emerald-50/60'
                  : 'bg-white hover:bg-slate-50'
              }`}
              data-testid={`checklist-item-${item.id}`}
            >
              <button
                onClick={() => handleToggle(item.id, item.completed)}
                disabled={togglingId === item.id}
                className="flex-shrink-0 transition-transform hover:scale-110"
                data-testid={`toggle-item-${item.id}`}
              >
                {togglingId === item.id ? (
                  <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                ) : item.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300 hover:text-amber-500" />
                )}
              </button>
              
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  item.completed ? 'text-slate-400 line-through' : 'text-slate-800'
                }`}>
                  {item.title}
                </p>
                {item.completed && item.completed_at && (
                  <div className="flex items-center gap-3 mt-0.5">
                    {editingDateId === item.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="datetime-local"
                          value={editDateValue}
                          onChange={(e) => setEditDateValue(e.target.value)}
                          className="text-xs border rounded px-2 py-0.5"
                          data-testid={`date-input-${item.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-2 text-xs text-emerald-600"
                          onClick={() => handleDateEdit(item.id)}
                          data-testid={`date-save-${item.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-2 text-xs text-slate-400"
                          onClick={() => setEditingDateId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="text-xs text-emerald-600 flex items-center gap-1 hover:text-emerald-700 hover:underline"
                        onClick={() => {
                          setEditingDateId(item.id);
                          const d = new Date(item.completed_at);
                          setEditDateValue(d.toISOString().slice(0, 16));
                        }}
                        data-testid={`date-edit-btn-${item.id}`}
                      >
                        <Calendar className="w-3 h-3" />
                        {formatDateTime(item.completed_at)}
                        <Pencil className="w-2.5 h-2.5 ml-0.5 opacity-60" />
                      </button>
                    )}
                    {item.completed_by && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.completed_by}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!item.is_default && (
                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 mr-1">Custom</Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-300 hover:text-red-500"
                  onClick={() => handleDelete(item.id)}
                  data-testid={`delete-item-${item.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No checklist items yet</p>
          </div>
        )}

        {/* Add custom task */}
        <form onSubmit={handleAddTask} className="flex gap-2 pt-4 border-t mt-4" data-testid="add-task-form">
          <Input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a custom task..."
            className="flex-1"
            data-testid="new-task-input"
          />
          <Button
            type="submit"
            disabled={adding || !newTask.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="add-task-btn"
          >
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
